import {
    BaseEntity,
    LoadDataPropsAsync,
    UpdateDataPropsAsync,
    registerGlobalFetchErrorHandler,
    useFetch,
    useFirstRender,
} from '@plusng/react-common-components';
import FileSaver from 'file-saver';
import { useJwtCreationContext } from '../../components/contexts/JwtCreationContext';
import { errorNotification, successNotification } from '../../utils/notifications';
import { getLocallyStoredUser, getSkipVersionCheckUrl } from '../../utils/utils';
import { useTranslationContext } from '../i18n/TranslationProvider';
import { useTranslation } from '../i18n/useTranslation';
import { handleFetchError } from './errorHandling';
import { isDevOrLocalEnv } from '../../configuration/confUtils';
import { useRetryRequestContext } from '../../components/contexts/RetryRequestContext';
import { RequestResolutionOptions, UploadFileOptions } from '../../utils/interfaces';
import { useNavigation } from '../../utils/customHooks';
import { useQueryClient } from '@tanstack/react-query';
import { ConflictReason } from '../../utils/enums';
import { Routes } from '../../routes/routes';

interface InvalidParams {
    reason: string;
}
interface FetchError {
    detail: string;
    instance: string;
    status: number;
    title: string;
    type: string;
    invalidParams: Array<InvalidParams>;
    reason?: string;
}

let errorStatus: number;

export function getErrorStatus() {
    return errorStatus;
}

export function useRestOperations<T extends BaseEntity | object | null>() {
    const firstRender = useFirstRender();

    const { openOverwriteDialog, configureNextRequest } = useRetryRequestContext();

    let activeFileFetches = 0;

    // register global fetch error handler
    registerGlobalFetchErrorHandler({
        errorHandler: async (errorResponse: Response) => {
            try {
                if (firstRender && errorResponse.status === 401) {
                    // To do not show error for the first time the app opened
                    return;
                }

                if (errorResponse.status === 403) {
                    errorStatus = errorResponse.status;
                    await navigateTo(Routes.admin_missing_permission);
                    return;
                }

                if (errorResponse.status === 401) {
                    errorStatus = errorResponse.status;
                }

                const error = (await errorResponse.json()) as FetchError;
                if (error) {
                    if (error.invalidParams) {
                        errorNotification(error.invalidParams[0].reason);
                    } else {
                        errorNotification(error.detail);
                    }
                }
            } catch (err) {
                // workaround because there is a bug in package @plusng/react-common-components
                // errorResponse.json() throws an exception because body stream is already read
                if (!(err instanceof TypeError)) {
                    console.error('Failed to process error response in global fetch handler.');
                }
            }
        },
    });
    const { createFakeJwt } = useJwtCreationContext();
    const { langState } = useTranslationContext();
    const { loadDataAsync } = useFetch<T>();
    const translate = useTranslation();
    const navigateTo = useNavigation();
    const queryClient = useQueryClient();

    const isDataConflictError = (error?: FetchError) => {
        if (!error) {
            return false;
        }
        return error?.reason === ConflictReason.DATA_CONFLICT;
    };

    const getReasonMessage = (reason?: string) => {
        switch (reason) {
            case 'RENEWAL_INVALID_FORMULATION_STATE':
                return translate('product_approval_reason_renewal_invalid_formulation_state');
            case 'RENEWAL_DRAFT_MISSING':
                return translate('product_approval_reason_renewal_draft_missing');
            case 'RENEWAL_APPROVAL_ALREADY_OPEN':
                return translate('product_approval_reason_renewal_approval_already_open');
            case 'ADDITIONAL_SHEET_BLOCKED_BY_RENEWAL_ALL':
                return translate('product_approval_reason_additional_sheet_blocked_by_renewal_all');
            case 'ADDITIONAL_SHEET_BLOCKED_BY_RENEWAL_REFERENCE':
                return translate('product_approval_reason_additional_sheet_blocked_by_renewal_reference');
            case 'ADDITIONAL_SHEET_PREREQUISITES_NOT_MET':
                return translate('product_approval_reason_additional_sheet_prerequisites_not_met');
            case 'PRIMARY_PRODUCT_PREREQUISITES_NOT_MET':
                return translate('product_approval_reason_primary_product_prerequisites_not_met');
            default:
                return undefined;
        }
    };

    const isFileConflictErrors = (error?: FetchError) => {
        if (!error) {
            return false;
        }
        return error?.reason === ConflictReason.FILE_CONFLICT || error?.reason === ConflictReason.DATA_CONFLICT;
    };

    async function doGetRequest(props: LoadDataPropsAsync) {
        props.customHeaderList = [{ name: 'Accept-Language', value: langState }];

        const user = getLocallyStoredUser();
        if (isDevOrLocalEnv() && user?.id !== undefined) {
            const jwt = await createFakeJwt();
            props.customHeaderList = [...props.customHeaderList, { name: 'Authorization', value: `Bearer ${jwt}` }];
        }
        /* Explanation:
        The loadDataAsync function returns a Promise that resolves to a value of type T | T[] | undefined because:
        - T[]: The API might return an array of objects of type T when multiple items are found.
        - T: The API might return a single object of type T when only one item is found.
        - undefined: The API might return undefined if no data is found or an error occurs.
        
        Additionally, even if the backend returns a single object {}, 
        loadDataAsync might wrap it in an array [{}] to maintain a consistent return type (always an array).
        We need to handle each possible case to correctly update the state. */

        const response = await loadDataAsync(props);
        if (!response) {
            console.error('Received undefined response from loadDataAsync');
            return;
        }
        return response;
    }
    /**
     * Performs an update data request with conflict handling.
     * This function merges the standard update parameters with {@link RequestResolutionOptions},
     * @param props - Combined update parameters and {@link RequestResolutionOptions}.
     *   - `navigateTo`: Optional route to navigate to after resolving a 409 or handling a 404.
     *   - `onConflict`: Optional conflict handler invoked when a 409 (Conflict) occurs.
     *   - `skipGeneralErrorHandling`: If true, disables automatic HTTP error handling
     *     (409, 400, 404) so the caller can manage it manually.
     * @returns A promise that resolves to the updated data of type T.
     * @throws An error if the request fails or if conflict resolution is not handled.
     */
    async function doUpdateDataRequest(props: UpdateDataPropsAsync<T> & RequestResolutionOptions): Promise<Awaited<T>> {
        props.customHeaderList = [{ name: 'Accept-Language', value: langState }];
        if (isDevOrLocalEnv()) {
            const jwt = await createFakeJwt();
            props.customHeaderList = [...props.customHeaderList, { name: 'Authorization', value: `Bearer ${jwt}` }];
        }
        try {
            const response = await fetch(props.queryString, {
                method: props.method ?? 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(props.customHeaderList || []).reduce(
                        (acc, h) => {
                            acc[h.name] = h.value;
                            return acc;
                        },
                        {} as Record<string, string>
                    ),
                },
                body: JSON.stringify(props.data),
            });
            const responseJson = await response.json();
            if (!response.ok) {
                //general error handling for  (409,404)
                if (!props.skipGeneralErrorHandling) {
                    await handleHttpErrorResolutionForUpdateData(
                        response.status,
                        isDataConflictError(responseJson as FetchError),
                        props
                    );
                }
                const error = responseJson as FetchError;
                if (error) {
                    if (error.invalidParams) {
                        errorNotification(error.invalidParams[0].reason);
                    } else if (getReasonMessage(error.reason)) {
                        errorNotification(getReasonMessage(error.reason) ?? '');
                    } else {
                        errorNotification(error.detail);
                    }
                }
                throw response;
            }

            const result: Awaited<T> = responseJson;
            return result;
        } catch (err) {
            if (!(err instanceof TypeError)) {
                console.error('Failed to process error response in global fetch handler.');
            }
            throw err;
        }
    }

    async function doDownloadFile(apiUrl: string, fileName?: string) {
        try {
            const headers = new Headers();
            headers.append('Accept-Language', langState);

            if (isDevOrLocalEnv()) {
                const jwt = await createFakeJwt();
                headers.append('Authorization', `Bearer ${jwt}`);
            }

            const result = await fetch(apiUrl, {
                method: 'GET',
                headers: headers,
            });
            if (!result.ok) {
                if (result.status === 404) {
                    await queryClient.invalidateQueries();
                }
                throw result;
            }

            const blob = await result.blob();
            FileSaver.saveAs(blob, fileName, { autoBom: false });
        } catch (error) {
            errorNotification(translate('error_loading_files'));
            throw error;
        }
    }

    async function doDownloadFileObject(file: File) {
        const fileUrl = URL.createObjectURL(file);

        // Create an anchor element dynamically
        const anchor = document.createElement('a');
        anchor.href = fileUrl;
        anchor.download = file.name; // Preserve the original file name
        anchor.target = '_blank'; // Open in a new tab

        // Programmatically click the anchor to trigger the download/open action
        anchor.click();

        // Optionally revoke the object URL to free up memory
        URL.revokeObjectURL(fileUrl);
    }

    // Create headers for file upload requests
    const createUploadHeaders = async (): Promise<Headers> => {
        const headers = new Headers();
        headers.append('Accept-Language', langState);
        if (isDevOrLocalEnv()) {
            const jwt = await createFakeJwt();
            headers.append('Authorization', `Bearer ${jwt}`);
        }
        return headers;
    };
    // Build FormData for a single file
    const buildFormData = <TBody>(file: File, body?: TBody): FormData => {
        const fd = new FormData();

        if (body !== undefined) {
            fd.append('body', new Blob([JSON.stringify(body)], { type: 'application/json' }));
        }
        fd.append('file', file);
        return fd;
    };
    // Build upload promises for multiple files
    const buildUploadPromises = <TBody>(
        files: File[],
        apiUrl: string,
        method: string,
        headers: Headers,
        body?: TBody
    ): Promise<Response>[] => {
        return files.map((file) =>
            fetch(apiUrl, {
                method,
                headers,
                body: buildFormData(file, body),
            })
        );
    };
    // Notify error from a non-ok fetch response
    async function notifyErrorFromRequestNotOk(res: Response): Promise<void> {
        const error = await res.json();
        if (!error) {
            return;
        }

        if (Array.isArray(error.invalidParams) && error.invalidParams.length > 0) {
            errorNotification(error.invalidParams[0]?.reason);
        } else if (getReasonMessage(error.reason)) {
            errorNotification(getReasonMessage(error.reason) ?? '');
        } else if (error.detail) {
            errorNotification(error.detail);
        }
    }

    /**
     * Upload files with conflict handling. using multipart/form-data.
     *
     * @typeParam TBody - The shape of the optional JSON body that will be appended as a part named "body".
     * @param options - Configuration options for the upload.
     * @returns The parsed JSON response from the server, if available; otherwise `void`.
     * @throws Error - Thrown on network errors, validation failures, or when a 409 conflict cannot be resolved.
     */
    async function doUploadFile<TBody>(uploadFileOptions: UploadFileOptions<TBody>) {
        try {
            window.dispatchEvent(new CustomEvent('fetch-start'));

            activeFileFetches++;
            const { files, apiUrl, method = 'POST', showSuccessMessage = true, bodyParams } = uploadFileOptions;

            const headers = await createUploadHeaders();

            const results = await Promise.all(buildUploadPromises(files, apiUrl, method, headers, bodyParams));

            const conflict = results.find((r) => r.status === 409);
            // handle conflict error for upload file (409)
            if (conflict) {
                await handleConflictErrorForUploadFile(uploadFileOptions, conflict);
                throw conflict;
            }

            const firstError = results.find((r) => !r.ok);
            if (firstError) {
                await notifyErrorFromRequestNotOk(firstError);
                // if there is a bad request (400) -> refetch all files
                if (results.some((r) => r.status === 400)) {
                    await queryClient.invalidateQueries();
                }
                throw firstError;
            }

            if (showSuccessMessage) {
                successNotification(translate('upload_file_success'));
            }
        } catch (error) {
            if (error instanceof Response) {
                await handleFetchError(error, 'Upload failed.');
            }
            throw error;
        } finally {
            activeFileFetches--;
            if (activeFileFetches === 0) {
                window.dispatchEvent(new CustomEvent('fetch-end'));
            }
        }
    }

    // Handle conflict error (409) for file uploads
    const handleConflictErrorForUploadFile = async (
        uploadFileOptions: UploadFileOptions,
        conflictResponse: Response
    ) => {
        const responseJson = (await conflictResponse.clone().json()) as FetchError;
        const overwrite = uploadFileOptions.onConflict?.overwrite ?? true;

        if (isFileConflictErrors(responseJson)) {
            if (!overwrite) {
                configureNextRequest({
                    isUploadFiles: true,
                    onConflict: uploadFileOptions.onConflict,
                    refetchFiles: true,
                });
            } else {
                configureNextRequest({
                    isUploadFiles: true,
                    showSuccessMessage: false,
                    refetchFiles: true,
                    onConflict: {
                        ...uploadFileOptions.onConflict,
                        uploadFilesOptions: uploadFileOptions.onConflict?.uploadFilesOptions ?? [
                            {
                                queryString: uploadFileOptions.apiUrl,
                                files: uploadFileOptions.files,
                                method: uploadFileOptions.method ?? 'POST',
                            },
                        ],
                    },
                    doUploadFile: doUploadFile,
                });
            }
            openOverwriteDialog();
        }
        if (Array.isArray(responseJson.invalidParams) && responseJson.invalidParams.length > 0) {
            errorNotification(responseJson.invalidParams[0]?.reason);
        } else if (getReasonMessage(responseJson.reason)) {
            errorNotification(getReasonMessage(responseJson.reason) ?? '');
        } else if (responseJson.detail) {
            errorNotification(responseJson.detail);
        }
    };
    // Handle HTTP errors (409, 404) for data update requests
    const handleHttpErrorResolutionForUpdateData = async (
        status: number,
        isDataConflictError: boolean,
        props: UpdateDataPropsAsync<T> & RequestResolutionOptions
    ) => {
        const overwrite = props.onConflict?.overwrite ?? true;

        if (status === 409 && !overwrite) {
            configureNextRequest({
                onConflict: props.onConflict,
            });
            openOverwriteDialog();
        }

        if (status === 409 && isDataConflictError && overwrite) {
            // Save the current request function for later retry
            const url = getSkipVersionCheckUrl(props.queryString);
            const retryProps = { ...props, queryString: url };
            configureNextRequest({
                doUpdateData: () => doUpdateDataRequest(retryProps),
                refetchFiles: false,
                showSuccessMessage: true,
                onConflict: props.onConflict,
                doUploadFile: doUploadFile,
                navigateTo: props.navigateTo,
            });
            openOverwriteDialog();
        }

        if (status === 404) {
            if (props.navigateTo) {
                await navigateTo(props.navigateTo);
            }
            await queryClient.invalidateQueries();
        }
    };

    return { doGetRequest, doUpdateDataRequest, doDownloadFile, doDownloadFileObject, doUploadFile };
}
